import { Fragment, useId, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useCanWrite } from "../auth/AuthProvider";
import { FilterChips } from "../components/FilterChips";
import { DeleteWorkloadsModal } from "../components/DeleteWorkloadsModal";
import { Modal } from "../components/Modal";
import { Pagination } from "../components/Pagination";
import { SearchInput } from "../components/SearchInput";
import { ServerPicker } from "../components/ServerPicker";
import {
  Alert,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  Mono,
  PageHeader,
  Select,
  SuspendedBadge,
  Table,
  TableSkeleton,
  Tag,
  Td,
  Th,
  TypedPhrase,
  cx,
} from "../components/ui";
import { ApiError } from "../lib/api";
import { formatBytes, formatDateTime, formatNumber, formatRelative } from "../lib/format";
import { hourlyMicros, money, rate } from "../lib/money";
import {
  useChangeDatabasePlan,
  useDatabaseAction,
  useDatabases,
  useDeleteDatabase,
  useFailoverDatabaseHa,
  useHoldDatabaseSwitchover,
  usePlans,
  useRepublishDatabaseHa,
  useSwitchDatabaseHa,
} from "../lib/queries";
import type { AdminDatabase, AdminDatabaseHa, DatabaseEngine, WorkloadResult } from "../lib/types";

const LIMIT = 25;

/** The platform's own words, cased the way the product writes them. */
const ENGINES: { value: DatabaseEngine; label: string }[] = [
  { value: "postgres", label: "Postgres" },
  { value: "mysql", label: "MySQL" },
  { value: "mariadb", label: "MariaDB" },
  { value: "mongo", label: "Mongo" },
  { value: "redis", label: "Redis" },
];

/**
 * Every managed database on the platform.
 *
 * The console had no cross-tenant view of these at all: the platform sold them
 * and could not list them, so "which databases are on the server we are about
 * to restart" was a question only SQL could answer.
 *
 * Deliberately without a state column, exactly as the app list is. The stored
 * `state` is written when an action is requested and never written back, so a
 * database whose container died still reads `running`, and resolving the truth
 * costs an engine call per row across every server in the fleet. Showing
 * nothing beats showing a value we know to be wrong.
 *
 * What it does show that the app list has no equivalent of is reachability. A
 * published port is open to anyone who finds it, and the public security page
 * states how many exist — a number stated in public should come from somewhere
 * an operator can see too.
 */
export function DatabasesPage() {
  const canWrite = useCanWrite();
  const [q, setQ] = useState("");
  const [serverId, setServerId] = useState("");
  const [engine, setEngine] = useState<DatabaseEngine | "">("");
  const [offset, setOffset] = useState(0);
  /** The database whose tier is being changed, holding the modal open. */
  const [retiering, setRetiering] = useState<AdminDatabase | null>(null);
  /** The database being stopped or started on the tenant's behalf. */
  const [intervening, setIntervening] = useState<AdminDatabase | null>(null);
  /** The database being deleted on the tenant's behalf. */
  const [removing, setRemoving] = useState<AdminDatabase | null>(null);
  /** The row whose pair is open on this page. There is no database detail route. */
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isPending, error } = useDatabases({
    q,
    limit: LIMIT,
    offset,
    ...(serverId ? { serverId } : {}),
    ...(engine ? { engine } : {}),
  });

  // Any filter change returns to the first page. Page four of the old filter is
  // rarely page four of the new one, and is sometimes past the end of it.
  function reset<T>(setter: (value: T) => void) {
    return (value: T) => {
      setter(value);
      setOffset(0);
      setSelectedId(null);
    };
  }

  const filtered = Boolean(q || serverId || engine);

  return (
    <>
      <PageHeader
        title="Databases"
        description="Every managed database on the platform, across all tenants and servers."
      />

      <Card>
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-3">
          <SearchInput value={q} onChange={reset(setQ)} placeholder="Search database or org…" />

          <div className="w-full sm:max-w-52">
            <ServerPicker
              value={serverId}
              onChange={reset(setServerId)}
              emptyLabel="All servers"
              ariaLabel="Filter by server"
            />
          </div>

          <FilterChips
            value={engine}
            onChange={reset(setEngine)}
            options={ENGINES}
            allLabel="All engines"
            ariaLabel="Filter by engine"
          />

          {/* The count lives here rather than only in the pager, which hides
              itself on a single page — the answer to "how many are there" was
              then visible only when there were enough to need paging. */}
          {!isPending && !error && (
            <p className="ml-auto text-sm text-fg-subtle">
              {formatNumber(data.total)} {data.total === 1 ? "database" : "databases"}
              {filtered && " matching"}
            </p>
          )}
        </div>

        {isPending ? (
          <TableSkeleton columns={canWrite ? 8 : 7} />
        ) : error ? (
          <div className="p-5">
            <Alert>{error.message}</Alert>
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            title="No matching databases"
            description={filtered ? "Try relaxing the filters." : undefined}
          />
        ) : (
          <>
            <Table
              head={
                <tr>
                  <Th>Database</Th>
                  <Th>Organization</Th>
                  <Th priority="sm">Engine</Th>
                  {/* What it costs from, next to what it is. `md` rather than
                      `sm`: on a phone the security column below outranks the
                      billing one. */}
                  <Th priority="md">Plan</Th>
                  {/* Promoted to the same breakpoint as engine. It was the last
                      column to appear, which put the one security-relevant fact
                      on this page behind the widest viewport. */}
                  <Th priority="sm">Reachable from</Th>
                  <Th priority="md">Server</Th>
                  {/* Updated, not created: the API orders by it, and a column
                      that does not explain the order it is presented in is a
                      puzzle rather than a column. */}
                  <Th priority="lg">Updated</Th>
                  {canWrite && <Th />}
                </tr>
              }
            >
              {data.data.map((database) => (
                <Fragment key={database.id}>
                <tr className={cx("hover:bg-raised", selectedId === database.id && "bg-raised")}>
                  <Td>
                    {/* Not a link to a detail route — there isn't one. A name
                        with a pair opens that pair under the row; every other
                        name stays text, and the organization link is still the
                        way in. */}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      {database.ha ? (
                        <button
                          type="button"
                          className={cx(
                            "inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-left font-medium text-fg hover:underline",
                            selectedId === database.id && "underline",
                          )}
                          aria-expanded={selectedId === database.id}
                          aria-controls={`ha-${database.id}`}
                          onClick={() =>
                            setSelectedId((current) => (current === database.id ? null : database.id))
                          }
                        >
                          {database.name}
                          <Tag tone={haListTone(database.ha)}>HA</Tag>
                        </button>
                      ) : (
                        <span className="font-medium text-fg">{database.name}</span>
                      )}
                    </div>
                    {/* Below the breakpoint where the column exists, the fact
                        rides along with the name. A phone is where an operator
                        checks something quickly, which is the worst place to
                        silently drop "this one is open to the internet". */}
                    {database.externalPort !== null && (
                      <span className="mt-1 flex items-center gap-1 text-xs text-warn sm:hidden">
                        Open to the internet
                        <Mono>:{database.externalPort}</Mono>
                      </span>
                    )}
                  </Td>
                  <Td>
                    <Link
                      to={`/organizations/${database.organization.id}`}
                      className="hover:underline"
                    >
                      {database.organization.name}
                    </Link>
                    {database.organization.suspendedAt && (
                      <span className="ml-2">
                        <SuspendedBadge />
                      </span>
                    )}
                  </Td>
                  <Td priority="sm">
                    <Tag>{ENGINES.find((e) => e.value === database.engine)?.label ?? database.engine}</Tag>
                  </Td>
                  <Td className={database.plan ? "whitespace-nowrap" : "text-fg-subtle"} priority="md">
                    {database.plan?.name ?? "—"}
                  </Td>
                  <Td priority="sm">
                    {database.externalPort === null ? (
                      <span className="text-fg-subtle">Inside its project</span>
                    ) : (
                      /* Warn rather than neutral: this is the one row state an
                         operator should notice while scanning past it.
                         The port sits outside the badge rather than inside it.
                         Within, the two strings wrap against each other at the
                         width where this column first appears, and a badge
                         broken over two lines with a number pushed to its far
                         edge reads as a rendering fault rather than a warning. */
                      <span className="flex items-center whitespace-nowrap">
                        <Tag tone="warn">Internet</Tag>
                        <Mono className="ml-2 text-fg-subtle">:{database.externalPort}</Mono>
                      </span>
                    )}
                  </Td>
                  <Td className="text-fg-subtle" priority="md">
                    {database.server?.name ?? "—"}
                  </Td>
                  <Td className="text-fg-subtle" priority="lg">
                    {formatRelative(database.updatedAt)}
                  </Td>
                  {canWrite && (
                    <Td className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setRetiering(database)}>
                          Change plan
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setIntervening(database)}>
                          Stop / start
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setRemoving(database)}>
                          Delete
                        </Button>
                      </div>
                    </Td>
                  )}
                </tr>
                {selectedId === database.id && database.ha ? (
                  <tr className="bg-canvas hover:bg-canvas">
                    <td colSpan={canWrite ? 8 : 7} className="p-4">
                      <DatabaseHaPanel database={database} canWrite={canWrite} />
                    </td>
                  </tr>
                ) : null}
                </Fragment>
              ))}
            </Table>
            <Pagination
              total={data.total}
              limit={data.limit}
              offset={data.offset}
              onChange={(next) => {
                setOffset(next);
                setSelectedId(null);
              }}
            />
          </>
        )}
      </Card>

      {/* Not mounted at all below superadmin: a closed <dialog> still puts its
          markup — and its title — into the page for a control that role has no
          button to reach. */}
      {canWrite && <ChangePlanModal database={retiering} onClose={() => setRetiering(null)} />}
      {canWrite && <LifecycleModal database={intervening} onClose={() => setIntervening(null)} />}
      {canWrite && <DeleteModal database={removing} onClose={() => setRemoving(null)} />}
    </>
  );
}

/**
 * Deletes a tenant's database on their behalf, through the same dialog every
 * operator delete uses: the organization's name typed, and the outcome shown
 * row by row — including what the engine or the zone would not give up.
 */
function DeleteModal({
  database,
  onClose,
}: {
  database: AdminDatabase | null;
  onClose: () => void;
}) {
  const remove = useDeleteDatabase();
  const [results, setResults] = useState<WorkloadResult[] | null>(null);

  function close() {
    setResults(null);
    onClose();
  }

  return (
    <DeleteWorkloadsModal
      open={database !== null}
      onClose={close}
      title={database ? `Delete ${database.name}` : "Delete"}
      description={
        database
          ? `${database.organization.name}'s database. Apps still holding its connection string keep the string and lose the database.`
          : undefined
      }
      items={
        database
          ? [
              {
                kind: "database",
                id: database.id,
                name: database.name,
                detail: `${database.engine}${database.server ? ` · ${database.server.name ?? database.server.id}` : ""}`,
                flag: database.externalPort !== null ? `published :${database.externalPort}` : undefined,
              },
            ]
          : []
      }
      noun="database"
      confirmName={database?.organization.name ?? ""}
      confirmLabel="Delete database"
      run={async ({ force, otp }) => {
        if (!database) return;
        const { result } = await remove.mutateAsync({
          databaseId: database.id,
          orgId: database.organization.id,
          force,
          otp,
        });
        setResults([
          { kind: "database", id: database.id, name: database.name, deleted: true, warnings: result?.warnings ?? [] },
        ]);
      }}
      pending={remove.isPending}
      results={results}
      onDone={close}
    />
  );
}

/**
 * Re-tiers a tenant's database on their behalf.
 *
 * The catalogue offered is the same one the customer sees — active database
 * tiers only. An archived tier stays reachable through the customer's own
 * history, not through us quietly putting new workloads on retired pricing.
 */
function ChangePlanModal({
  database,
  onClose,
}: {
  database: AdminDatabase | null;
  onClose: () => void;
}) {
  const [planId, setPlanId] = useState("");
  const [error, setError] = useState<string | null>(null);
  // The catalogue is short; one page of 100 is all of it.
  const plans = usePlans({ limit: 100 });
  const change = useChangeDatabasePlan();

  const tiers = (plans.data?.data ?? []).filter(
    (plan) => plan.kind === "database" && plan.state === "active",
  );
  const selected = tiers.find((plan) => plan.id === planId);

  function close() {
    setPlanId("");
    setError(null);
    onClose();
  }

  async function save() {
    if (!database || !planId) return;
    setError(null);
    try {
      await change.mutateAsync({ databaseId: database.id, planId });
      close();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not change the plan.");
    }
  }

  return (
    <Modal
      open={database !== null}
      onClose={close}
      title={database ? `Change the plan of ${database.name}` : "Change plan"}
      description={
        database
          ? `${database.organization.name} pays for this database. The change lands in their audit log.`
          : undefined
      }
      dismissable={!change.isPending}
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <div className="space-y-1.5">
          <p className="text-sm text-fg-muted">
            Currently on{" "}
            <span className="font-medium text-fg">{database?.plan?.name ?? "no plan"}</span>.
          </p>
          <Select
            aria-label="New plan"
            value={planId}
            onChange={(event) => setPlanId(event.target.value)}
          >
            <option value="">Choose a tier…</option>
            {tiers.map((plan) => (
              <option key={plan.id} value={plan.id} disabled={plan.id === database?.plan?.id}>
                {plan.name}
                {plan.id === database?.plan?.id ? " (current)" : ""}
              </option>
            ))}
          </Select>
          {selected && (
            <p className="text-xs text-fg-subtle">
              {money(selected.price.cents * 10_000, selected.price.currency)}/mo ·{" "}
              {rate(hourlyMicros(selected.price.cents), selected.price.currency)}/hr, from the next
              billed hour. If backups are enabled, their 30% surcharge follows the new rate.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close} disabled={change.isPending}>
            Cancel
          </Button>
          <Button
            loading={change.isPending}
            disabled={!planId || planId === database?.plan?.id}
            onClick={() => void save()}
          >
            Move to this plan
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Stops or starts a tenant's database on their behalf.
 *
 * Both actions are always offered because this list deliberately shows no
 * running state — the stored value is written on request and never written
 * back, so a button that trusted it would sometimes be the wrong one. The
 * engine answers the request against the container's real state either way.
 */
function LifecycleModal({
  database,
  onClose,
}: {
  database: AdminDatabase | null;
  onClose: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const act = useDatabaseAction();

  function close() {
    setError(null);
    onClose();
  }

  async function run(action: "start" | "stop") {
    if (!database) return;
    setError(null);
    try {
      await act.mutateAsync({ databaseId: database.id, action });
      close();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : `Could not ${action} the database.`);
    }
  }

  return (
    <Modal
      open={database !== null}
      onClose={close}
      title={database ? `Stop or start ${database.name}` : "Stop or start"}
      description={
        database
          ? `${database.organization.name}'s database. Lands in their audit log. ` +
            "Stopping frees CPU and memory, not the disk — they are billed until they delete it."
          : undefined
      }
      dismissable={!act.isPending}
    >
      <div className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <p className="text-sm leading-6 text-fg-muted">
          The lever for a database that is filling its server — storage is the one figure a tier
          does not enforce. Stopping keeps the data and closes nothing permanently; the tenant can
          start it again themselves.
        </p>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close} disabled={act.isPending}>
            Cancel
          </Button>
          <Button variant="secondary" loading={act.isPending} onClick={() => void run("start")}>
            Start database
          </Button>
          <Button variant="danger" loading={act.isPending} onClick={() => void run("stop")}>
            Stop database
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * The pair for one database, on the list — there is no detail route.
 * Nothing here turns high availability on. Each action waits until the
 * database name is typed.
 */
export function DatabaseHaPanel({
  database,
  canWrite,
}: {
  database: AdminDatabase;
  canWrite: boolean;
}) {
  const hold = useHoldDatabaseSwitchover();
  const switchover = useSwitchDatabaseHa();
  const failover = useFailoverDatabaseHa();
  const republish = useRepublishDatabaseHa();
  const holdId = useId();
  const switchoverId = useId();
  const failoverId = useId();
  const republishId = useId();
  const ha = database.ha;
  if (!ha || typeof ha !== "object") return null;

  const isProtected = ha.protected === true;
  const busy = hold.isPending || switchover.isPending || failover.isPending || republish.isPending;

  return (
    <div id={`ha-${database.id}`}>
      <Card>
        <CardHeader
          title="High availability"
          description={`${database.name} · ${database.organization.name}. Nothing here turns it on.`}
        />
        <div className="space-y-5 px-5 py-4">
          <div className="flex flex-wrap items-center gap-2">
            <Tag tone={haStatusTone(ha.status)}>{haStatusLabel(ha.status)}</Tag>
            <Tag tone={isProtected ? "good" : "warn"}>{isProtected ? "Protected" : "Unprotected"}</Tag>
          </div>
          <dl className="grid grid-cols-2 gap-px border border-line bg-line sm:grid-cols-3 lg:grid-cols-5">
            <HaFact label="Generation">
              <Mono>{generationText(ha.generation)}</Mono>
            </HaFact>
            <HaFact label="Sync">{ha.syncState ? <Mono>{ha.syncState}</Mono> : "—"}</HaFact>
            <HaFact label="Lag">{formatLag(ha.lagBytes)}</HaFact>
            <HaFact label="Last failover">{formatDateTime(ha.lastFailoverAt)}</HaFact>
            <HaFact label="Hold">{ha.switchoverHold ? "Held" : "Off"}</HaFact>
          </dl>

          {canWrite && (
            <div className="space-y-5 border-t border-line pt-5">
              <div>
                <h3 className="text-sm font-medium text-fg">Routine</h3>
                <p className="mt-1 text-[13px] leading-5 text-fg-muted">
                  Type <TypedPhrase>{database.name}</TypedPhrase> on the action you mean. Holding switchover does not fail over.
                </p>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  <ConfirmedAction
                    name={database.name}
                    inputId={holdId}
                    explanation="Holds switchover so the writer stays where it is. This does not fail over."
                  >
                    {(ready) => (
                      <div className="space-y-2">
                        {hold.error && <Alert>{hold.error.message}</Alert>}
                        <div className="flex items-center gap-3">
                          <HoldSwitch
                            on={ha.switchoverHold === true}
                            disabled={!ready || busy}
                            onChange={(next) => hold.mutate({ databaseId: database.id, hold: next })}
                          />
                          <span className="text-sm text-fg">
                            {ha.switchoverHold ? "Switchover held" : "Switchover not held"}
                          </span>
                        </div>
                      </div>
                    )}
                  </ConfirmedAction>

                  <ConfirmedAction
                    name={database.name}
                    inputId={switchoverId}
                    explanation="This moves the writer back and waits for sync."
                  >
                    {(ready) => (
                      <div className="space-y-2">
                        {switchover.error && <Alert>{switchover.error.message}</Alert>}
                        <Button
                          variant="secondary"
                          disabled={!ready || !isProtected || busy}
                          loading={switchover.isPending}
                          onClick={() => switchover.mutate(database.id)}
                        >
                          Planned switchover
                        </Button>
                        {!isProtected && (
                          <p className="text-xs text-fg-subtle">Enabled only while this database is protected.</p>
                        )}
                      </div>
                    )}
                  </ConfirmedAction>
                </div>
              </div>

              <div className="border border-danger/30 p-4">
                <h3 className="text-sm font-medium text-fg">Break glass</h3>
                <p className="mt-1 text-[13px] leading-5 text-fg-muted">
                  Each of these asks for the name again.
                </p>
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  {ha.protected === false && (
                    <ConfirmedAction
                      name={database.name}
                      inputId={failoverId}
                      explanation="Force failover can cause data loss. The standby takes over without waiting for sync."
                    >
                      {(ready) => (
                        <div className="space-y-2">
                          {failover.error && <Alert>{failover.error.message}</Alert>}
                          <Button
                            variant="danger"
                            disabled={!ready || busy}
                            loading={failover.isPending}
                            onClick={() => failover.mutate(database.id)}
                          >
                            Force failover
                          </Button>
                        </div>
                      )}
                    </ConfirmedAction>
                  )}

                  <ConfirmedAction
                    name={database.name}
                    inputId={republishId}
                    explanation="The published port may change and external clients must use the new port if it does."
                  >
                    {(ready) => (
                      <div className="space-y-2">
                        {republish.error && <Alert>{republish.error.message}</Alert>}
                        <Button
                          variant="danger"
                          disabled={!ready || busy}
                          loading={republish.isPending}
                          onClick={() =>
                            republish.mutate({
                              databaseId: database.id,
                              confirmPort: database.externalPort,
                            })
                          }
                        >
                          Republish
                        </Button>
                      </div>
                    )}
                  </ConfirmedAction>
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}

function haListTone(ha: AdminDatabaseHa): "neutral" | "good" | "warn" | "bad" {
  if (ha.status === "failing_over") return "bad";
  if (ha.protected === true && ha.status === "healthy") return "good";
  if (ha.status === "degraded" || ha.protected === false) return "warn";
  return "neutral";
}

function ConfirmedAction({
  name,
  inputId,
  explanation,
  children,
}: {
  name: string;
  inputId: string;
  explanation: string;
  children: (ready: boolean) => ReactNode;
}) {
  const [typed, setTyped] = useState("");
  const ready = name.length > 0 && typed === name;
  return (
    <div className="space-y-2 border border-line bg-canvas p-3">
      <p className="text-[13px] leading-5 text-fg-muted">{explanation}</p>
      <Field
        label={
          <>
            Type <TypedPhrase>{name}</TypedPhrase> to confirm
          </>
        }
        htmlFor={inputId}
      >
        <Input
          id={inputId}
          value={typed}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setTyped(event.target.value)}
        />
      </Field>
      {children(ready)}
    </div>
  );
}

function HoldSwitch({
  on,
  disabled,
  onChange,
}: {
  on: boolean;
  disabled: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label="Hold switchover"
      disabled={disabled}
      onClick={() => onChange(!on)}
      className="-m-2 rounded-md p-2 focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span
        className={cx(
          "flex h-5 w-9 items-center rounded-full border transition-colors duration-200",
          on ? "border-accent bg-accent" : "border-line-strong bg-raised",
        )}
      >
        <span
          className={cx(
            "size-3.5 rounded-full transition-transform duration-200",
            on ? "translate-x-[18px] bg-white" : "translate-x-[2px] bg-fg-subtle",
          )}
        />
      </span>
    </button>
  );
}

function HaFact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="bg-canvas px-3 py-2.5">
      <dt className="text-xs text-fg-subtle">{label}</dt>
      <dd className="mt-1 text-sm text-fg">{children}</dd>
    </div>
  );
}

function haStatusLabel(status: string | undefined): string {
  if (!status) return "—";
  return status
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function haStatusTone(status: string | undefined): "neutral" | "good" | "warn" | "bad" {
  if (status === "healthy") return "good";
  if (status === "degraded") return "warn";
  if (status === "failing_over") return "bad";
  return "neutral";
}

function generationText(value: AdminDatabaseHa["generation"] | null | undefined): string {
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (typeof value === "string" && value.trim()) return value;
  return "—";
}

function formatLag(value: AdminDatabaseHa["lagBytes"] | undefined): string {
  if (value === null || value === undefined || value === "") return "—";
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount) || amount < 0) return "—";
  return formatBytes(amount);
}
